import type { NavTone } from '@/components/layout/shell/types';
import type { AnchorMeta, CorePort, ObserverEventKind, ObserverSettings } from '../observerContext';
import { EVENT_PLAN } from '../observerEvents';
import { makeContact, type ContactLayer } from './contact';
import {
  A_ABORT,
  A_BRAKE,
  A_STOP,
  A_STOP_HOME,
  ROUTE_MAX_MS,
  ROUTE_MIN_MS,
  bakeKeys,
  brakeTrack,
  buildTrack,
  cruise,
  indexAt,
  rootFrames,
  type PinnedTrack,
} from './kinematics';
import { boxOf, layoutMoving, readPlan, readTarget, shown, type PlanRead, type TargetRead } from './plan';
import { HOME_SKIP, makeProbe } from './probe';
import {
  SHELF_DY,
  attachPoint,
  buildGraph,
  easeExit,
  exitOf,
  findRoute,
  join,
  lengthOf,
  pinTail,
  reversed,
  routeFromOld,
  seatFor,
  splitCusps,
  streetsNear,
  type Graph,
} from './route';
import {
  dropOnly,
  type Box,
  type Contact,
  type CourierBody,
  type HousePlan,
  type PathPoint,
  type SeatGeom,
} from './types';

/*
  Курьер ядра (spec-3-motion): визит к месту события и обратно по плану дома.

  Порядок визита: проверки до вылета (план, посадка, место, путь, время, курсор, раскладка в покое) → дом
  отдаёт ядро (CorePort.release: знак набухает 100 мс, таблетка стягивается 220 мс) → в том же кадре капля
  курьера встаёт на место ядра → путь по улицам дома (WAAPI на root, тело — на tilt/skin) → перепроверка за
  120 мс до касания → касание: мениск, оседание и разворот тела, кольцо — только с «Кольцом на цели» → жест и
  удержание по EVENT_PLAN → подъём → обратно кратчайшим путём к ТЕКУЩЕМУ дому → приём (CorePort.receive) в
  одном кадре со скрытием капли.

  Прерывания (spec-3-motion §8): дом сменился — удержание обрывается, путь пересобирается к новому дому с той
  же скоростью (нужный узел позади — торможение и разворот); прокрутка — слой attach (вес 0 на рамке, 1 на
  полке и у цели), сдвиг больше 24 px — хвост пересобирается; цель пропала или не прошла перепроверку —
  торможение не дольше 125 мс и домой без посадки; ящик, поиск, ИИ поверх — сразу домой; событие старше —
  удержание не короче 400 мс; «Фокус» — единственное гашение: капля впитывается в кромку за 160 мс; вкладка
  браузера ушла в фон — визит снимается мгновенно. От цели к цели капля не перелетает никогда — только через дом.
*/

/** Перепроверка посадки до касания, мс. */
export const RECHECK_MS = 120;
/** Сдвиг цели в пути, после которого хвост пересобирается, px. */
export const REPLAN_SCROLL_PX = 24;
/** Окно цели не прокручивали столько, мс, — иначе ждём. */
export const SCROLL_QUIET_MS = 300;
/** Курсор ближе к точке посадки — полёта нет. */
export const CURSOR_CLEAR = 24;
/** Жест может продлить удержание не больше чем на столько, мс. */
export const REACT_EXTRA_MAX_MS = 800;
/** Событие старше: удержание не короче, мс после посадки. */
export const HOLD_MIN_MS = 400;
/** Подъём: кольцо стекает 240 мс, тело сворачивается с 80-й мс. */
export const LIFT_LAG_MS = 80;
/** «Фокус» во время визита: капля впитывается в кромку, мс. */
export const ABSORB_MS = 160;
/** Раскладка ещё едет после торможения: как часто смотреть и сколько ждать, мс. */
const SETTLE_TICK_MS = 50;
const SETTLE_MAX_MS = 800;
/** Переход раскладки начинается не в тот же кадр, что смена дома: столько ждём, прежде чем считать, что встала. */
const RELAYOUT_MIN_MS = 120;
/** Назад по пройденному: стык с улицей плана — не дальше, px; после неудачной пробы — шаг дальше, точек сетки. */
const JOIN_PX = 4;
const JOIN_SKIP = 4;

export interface VisitAnchor {
  el: HTMLElement;
  meta: AnchorMeta;
}

export interface VisitRequest {
  kind: ObserverEventKind;
  ids: string[];
  port: CorePort;
  anchors: (id: string) => readonly VisitAnchor[];
  settings: ObserverSettings;
  cursor: { x: number; y: number } | null;
}

export type VisitPlan =
  | { go: true; target: HTMLElement; start: () => Promise<void> }
  | { go: false; wait: boolean; reason: string; target: HTMLElement | null };

export interface CourierLayers {
  clip: HTMLElement;
  root: HTMLElement;
  attach: HTMLElement;
  tilt: HTMLElement;
  skin: HTMLElement;
  contactWrap: HTMLElement;
  contactSvg: SVGSVGElement;
  film: HTMLElement;
  routeSvg: SVGSVGElement;
}

type Phase = 'prep' | 'out' | 'contact' | 'hold' | 'lift' | 'back' | 'done';

type HomePoint = HousePlan['home'];

interface Leg {
  track: PinnedTrack;
  anims: Animation[];
  /** Куда ведёт: к цели, домой, торможение перед разворотом. */
  kind: 'out' | 'back' | 'brake';
  /** Нога ведёт к дому (обратный путь и торможение на нём): пройденное по ней — не путь назад. */
  home: boolean;
  /** После торможения: домой или к цели заново. */
  then?: 'home' | 'tail';
  /** Торможение на дороге домой: её остаток за точкой остановки (в координатах ноги). */
  ahead?: PathPoint[];
  /** Дом, к которому ведёт дорога домой. */
  to?: HomePoint;
  /** Дорога домой после разворота: куски с места, с остановкой перед каждым. */
  next?: PathPoint[][];
}

interface Films {
  meniscus?: { out(k: number): Promise<void> };
  ring?: { back(k: number): Promise<void>; off(): void };
  contour?: { off(k: number): Promise<void> };
}

interface Visit {
  req: VisitRequest;
  /** Адрес и роль якоря — чтобы перечитать цель после прокрутки и смены раскладки. */
  id: string;
  meta: AnchorMeta;
  shell: HTMLElement;
  /** «Замедлить ×4». */
  k: number;
  phase: Phase;
  target: TargetRead;
  seat: SeatGeom;
  size: 26 | 16 | 8;
  room: number;
  leg: Leg | null;
  /** Привязка attach: прямоугольники якоря и окна в момент построения трассы. */
  base: { anchor: Box; pane: Box | null; menu: number | null };
  /** Поправка attach: сдвиг якоря, окна и правой кромки меню с построения трассы. */
  shift: { ax: number; ay: number; px: number; mx: number };
  contact: Contact | null;
  films: Films;
  landedAt: number;
  holdEnd: number;
  reactUntil: number;
  timers: number[];
  /** Дом сменился, пока ядро отдавалось или садилось: сразу домой. */
  homeMoved: boolean;
  /** Дом уже принял ядро (приём — один раз). */
  received: boolean;
  /** Приём не сошёлся с домом — путь пересобран один раз. */
  retried: boolean;
  /** Тон капли сейчас и цвет финала события (без окраски дома капля несёт его одна). */
  tone: NavTone;
  finale: NavTone;
  /** Где был дом при вылете: тот же дом при новой раскладке — не смена дома. */
  homeAt: HomePoint | null;
  /** Дом, из которого вылетела капля (не меняется). */
  from: HomePoint;
  /** Пройденное от дома (в экранных координатах на тот миг): по нему капля возвращается, если улиц нет. */
  trail: PathPoint[];
  /** Нога, которой капля села на цель: пройденное на касании и удержании. */
  landed: PinnedTrack | null;
  /** Пришло событие старше: удержание не дольше 400 мс после посадки. */
  hurried: boolean;
  /** Раскладка поехала (меню свернули, выехала зона): когда встанет, остаток пути пересобирается. */
  relayoutAt: number | null;
  /** Левая кромка окна цели при прошлой проверке после торможения: окно ещё едет — раскладка не встала. */
  paneLeft: number | null;
  resolve: () => void;
}

/** Правая кромка главного меню (порог тянется с ней). */
const menuRight = (shell: HTMLElement) =>
  shell.querySelector('[data-shell-card="menu"]')?.getBoundingClientRect().right ?? null;

const inBox = (b: Box, x: number, y: number) => x >= b.left && x <= b.right && y >= b.top && y <= b.bottom;

/** Вес поправки attach по привязке точки трассы: рамка 0, стена — сдвиг окна, полка и цель — сдвиг якоря. */
const pinWeights = (pin: number) =>
  pin <= 1 ? { pane: pin, anchor: 0 } : { pane: 2 - pin, anchor: pin - 1 };
/** Поправка attach точки пути: порог тянется с правой кромкой меню (не с окном — между ними может выехать зона). */
const offsetOf = (pin: number, lane: string, s: Visit['shift']) => {
  if (lane === 'sill') return { x: pin * s.mx, y: 0 };
  const w = pinWeights(pin);
  return { x: w.pane * s.px + w.anchor * s.ax, y: w.anchor * s.ay };
};

export class Courier {
  private body: CourierBody | null = null;
  private bodyMounted = false;
  /** Тело, сменённое во время визита: встаёт после приёма. */
  private nextBody: CourierBody | null = null;
  private visit: Visit | null = null;
  private raf = 0;
  private dirty = true;
  private scrollAt = new WeakMap<EventTarget, number>();
  private contact: ContactLayer;
  private showRoute = false;
  private off: (() => void)[] = [];
  /** Отлучка ядра: провайдер держит away (ритм ожидания — только дома). */
  onAway: (away: boolean) => void = () => undefined;

  constructor(private L: CourierLayers) {
    this.contact = makeContact(L.contactSvg, L.film);
    const onScroll = (e: Event) => {
      this.scrollAt.set(e.target ?? window, performance.now());
      this.dirty = true;
    };
    const onDirty = () => {
      this.dirty = true;
    };
    const onHidden = () => {
      if (document.hidden) this.cancel();
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onDirty);
    document.addEventListener('transitionrun', onDirty, true);
    document.addEventListener('transitionend', onDirty, true);
    document.addEventListener('visibilitychange', onHidden);
    this.off.push(() => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onDirty);
      document.removeEventListener('transitionrun', onDirty, true);
      document.removeEventListener('transitionend', onDirty, true);
      document.removeEventListener('visibilitychange', onHidden);
    });
    this.hideDrop();
  }

  destroy() {
    this.cancel();
    this.off.forEach((f) => f());
    if (this.body && this.bodyMounted) this.body.unmount();
    this.bodyMounted = false;
  }

  /** Сменить тело (проп провайдера body): старое снимается, новое встаёт к следующему визиту. */
  setBody(body: CourierBody) {
    if (this.visit) {
      // Визит доигрывает тем телом, что вылетело; смена — после приёма (finish).
      this.nextBody = body === this.body ? null : body;
      return;
    }
    if (this.body === body) return;
    this.dropBody();
    this.body = body;
  }

  /** Снять тело целиком: следующий визит смонтирует его заново (из любого состояния — капля). */
  private dropBody() {
    if (this.body && this.bodyMounted) this.body.unmount();
    this.bodyMounted = false;
  }

  busy() {
    return this.visit !== null;
  }

  /* ----- Стенд: «Показать дорогу» ----- */

  setShowRoute(on: boolean, port: CorePort | null) {
    this.showRoute = on;
    if (!on) this.L.routeSvg.replaceChildren();
    else if (port) this.drawStreets(port);
  }

  /** Улицы дома пунктиром по текущей раскладке; маршрут — сплошной; причина отказа — подписью у дома. */
  drawStreets(port: CorePort, seat: SeatGeom | null = null, reason?: string) {
    if (!this.showRoute) return;
    const read = readPlan(port);
    if (read) this.drawGraph(read, buildGraph(read.plan, seat), undefined, seat, reason);
  }

  private drawGraph(read: PlanRead, g: Graph, route?: PathPoint[], seat?: SeatGeom | null, reason?: string) {
    if (!this.showRoute) return;
    const svg = this.L.routeSvg;
    const NS = 'http://www.w3.org/2000/svg';
    svg.replaceChildren();
    const poly = (pts: PathPoint[], cls: string, dash: boolean) => {
      const p = document.createElementNS(NS, 'path');
      p.setAttribute('d', pts.map((q, i) => `${i ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' '));
      p.setAttribute('fill', 'none');
      p.setAttribute('stroke-width', '1');
      if (dash) p.setAttribute('stroke-dasharray', '3 3');
      p.setAttribute('class', cls);
      svg.appendChild(p);
    };
    for (const s of g.streets) poly(s, 'stroke-line-strong', true);
    if (route) poly(route, 'stroke-brand-fg', false);
    if (seat) {
      const c = document.createElementNS(NS, 'circle');
      c.setAttribute('cx', `${seat.x}`);
      c.setAttribute('cy', `${seat.y}`);
      c.setAttribute('r', '3');
      c.setAttribute('fill', 'none');
      c.setAttribute('class', 'stroke-brand-fg');
      svg.appendChild(c);
    }
    if (reason) {
      const t = document.createElementNS(NS, 'text');
      t.setAttribute('x', `${read.plan.menu.box.right + 8}`);
      t.setAttribute('y', `${read.plan.ceiling + 10}`);
      t.setAttribute('class', 'fill-fg-muted text-2xs');
      t.textContent = `Помощник: ${reason}`;
      svg.appendChild(t);
    }
  }

  /* ----- Проверки до вылета (spec-3-motion §4) ----- */

  plan(req: VisitRequest): VisitPlan {
    // Вкладка браузера в фоне: анимации стоят, визит доиграл бы, когда человек вернётся, — полёта нет.
    if (document.hidden) return { go: false, wait: false, reason: 'вкладка в фоне', target: null };
    const read = readPlan(req.port);
    if (!read) return { go: false, wait: false, reason: 'нет плана дома', target: null };
    if (layoutMoving(read.shell))
      return { go: false, wait: true, reason: 'раскладка в движении', target: null };
    // Всплывающий слой доигрывает выход (меню закрывается) — через миг он уйдёт и не закроет путь.
    if (document.querySelector('[data-radix-popper-content-wrapper] > [data-state="closed"]')) {
      return { go: false, wait: true, reason: 'закрывается слой поверх', target: null };
    }
    const probe = makeProbe(read);
    const now = performance.now();
    let reason = 'нет цели на экране';
    /** Почему отказал каждый якорь цепочки — подписью у дома на стенде. */
    const why: string[] = [];
    const no = (id: string, r: string) => {
      reason = r;
      why.push(`${id}: ${r}`);
    };
    let refused: HTMLElement | null = null;
    let lastSeat: SeatGeom | null = null;
    for (const id of req.ids) {
      for (const a of req.anchors(id)) {
        const t = readTarget(read, id, a.el, a.meta);
        if (typeof t === 'string') {
          no(id, t);
          continue;
        }
        refused = t.el;
        const quiet = t.geom.kind === 'menu' ? null : (t.scroll ?? t.el.closest('[role="tablist"]'));
        if (quiet && now - (this.scrollAt.get(quiet) ?? -Infinity) < SCROLL_QUIET_MS) {
          return { go: false, wait: true, reason: 'окно цели прокручивают', target: t.el };
        }
        const seat = seatFor(read.plan, t.geom, req.settings.seat);
        if (typeof seat === 'string') {
          no(id, seat);
          continue;
        }
        lastSeat = seat;
        const c = req.cursor;
        if (
          c &&
          (inBox(boxOf(t.el.getBoundingClientRect()), c.x, c.y) ||
            Math.hypot(c.x - seat.x, c.y - seat.y) < CURSOR_CLEAR)
        ) {
          this.drawStreets(req.port, seat, 'курсор у цели');
          return { go: false, wait: false, reason: 'курсор у цели', target: t.el };
        }
        let size: 26 | 16 | 8 = 8;
        let room = 12;
        if (!dropOnly(seat.kind)) {
          const clip = this.clipOf(read, t, seat);
          room = probe.room(seat, clip, this.anchorEl(t));
          size = room >= 26 ? 26 : room >= 16 ? 16 : 8;
          if (size === 8) {
            no(id, `тесно у посадки (${Math.round(room)} px)`);
            continue;
          }
          const bad = probe.landing(seat, size, room, clip, this.anchorEl(t));
          if (bad) {
            no(id, bad);
            continue;
          }
        }
        const g = buildGraph(read.plan, seat);
        if (g.refused || !g.seat) {
          no(id, g.refused ?? 'нет посадки на плане');
          continue;
        }
        const route = findRoute(g, 'home', g.seat);
        if (!route) {
          no(id, 'нет пути по улицам');
          continue;
        }
        const pts = seat.kind === 'menu' ? pinTail(route.pts, HOME_SKIP) : route.pts;
        const track = buildTrack(pts, { vc: cruise(route.length), aStop: A_STOP, minMs: ROUTE_MIN_MS });
        if (track.duration > ROUTE_MAX_MS) {
          no(id, `далеко (${Math.round(track.duration)} мс)`);
          continue;
        }
        const busy = probe.route(pts, { start: true, end: false });
        if (busy) {
          no(id, busy);
          continue;
        }
        this.drawGraph(read, g, route.pts, seat);
        const dir: 'up' | 'down' | 'left' =
          read.plan.home.kind === 'pill' ? 'left' : route.nodes[1] === 'roof:jet' ? 'up' : 'down';
        const visit = (resolve: () => void): Visit => ({
          req,
          id,
          meta: a.meta,
          shell: read.shell,
          k: req.settings.slowMo ? 4 : 1,
          phase: 'prep',
          target: t,
          seat,
          size,
          room,
          leg: null,
          base: {
            anchor: boxOf(this.anchorEl(t).getBoundingClientRect()),
            pane: this.paneBox(t),
            menu: menuRight(read.shell),
          },
          shift: { ax: 0, ay: 0, px: 0, mx: 0 },
          contact: null,
          films: {},
          landedAt: 0,
          holdEnd: 0,
          reactUntil: 0,
          timers: [],
          homeMoved: false,
          received: false,
          retried: false,
          tone: 'brand',
          finale: EVENT_PLAN[req.kind].ring === 'success' ? 'success' : 'brand',
          homeAt: { ...read.plan.home },
          from: { ...read.plan.home },
          trail: [],
          landed: null,
          hurried: false,
          relayoutAt: null,
          paneLeft: null,
          resolve,
        });
        return {
          go: true,
          target: t.el,
          start: () => new Promise<void>((resolve) => this.begin(visit(resolve), track, dir)),
        };
      }
    }
    this.drawStreets(req.port, lastSeat, why.join(' · ') || reason);
    return { go: false, wait: false, reason, target: refused };
  }

  private clipOf(read: PlanRead, t: TargetRead, seat: SeatGeom): Box {
    if (seat.kind === 'tab') {
      const P = read.plan.panes[seat.pane];
      return { left: P.tabs.left, right: P.tabs.right, top: read.plan.ceiling, bottom: P.strip.bottom };
    }
    return boxOf(t.scroll!.getBoundingClientRect());
  }

  /** Элемент, за которым следит attach: у строки — её блок (тело на нём). */
  private anchorEl(t: TargetRead) {
    return t.block ?? t.el;
  }

  private paneBox(t: TargetRead): Box | null {
    return t.pane ? boxOf(t.pane.getBoundingClientRect()) : null;
  }

  /* ----- Ход визита ----- */

  private later(v: Visit, fn: () => void, ms: number) {
    v.timers.push(window.setTimeout(fn, ms));
  }

  private ensureBody(): CourierBody | null {
    if (!this.body) return null;
    if (!this.bodyMounted) {
      this.body.mount(this.L.tilt, this.L.skin);
      this.bodyMounted = true;
    }
    return this.body;
  }

  private setTone(tone: NavTone) {
    if (tone === 'brand') delete this.L.clip.dataset.tone;
    else this.L.clip.dataset.tone = tone;
  }

  /**
   * Цвет капли: уходит и возвращается в тоне дома (шов передачи без смены цвета). Дом без окраски по
   * состоянию (коралл) — у цели капля в цвете финала («готово» — зелёная), обратно остывает до коралла в пути.
   */
  private followTone(v: Visit) {
    const home = v.req.port.origin()?.tone ?? 'brand';
    const back = v.phase === 'back' || v.phase === 'done' || v.phase === 'prep';
    const tone = back || home !== 'brand' ? home : v.finale;
    if (tone === v.tone && v.phase !== 'prep') return;
    v.tone = tone;
    // Домой — перелив тона кончается до приёма: в кадр передачи капля уже в тоне дома (шва цвета нет).
    const leg = v.leg;
    const left = back && leg ? (leg.track.duration - this.legTime(leg)) * v.k : Infinity;
    if (left < Infinity)
      this.L.clip.style.setProperty('--courier-tone-cap', `${Math.max(0, Math.round(left - 40))}ms`);
    else this.L.clip.style.removeProperty('--courier-tone-cap');
    this.setTone(tone);
  }

  private hideDrop() {
    const { root, tilt, attach, contactWrap, clip } = this.L;
    for (const el of [root, tilt, attach]) el.getAnimations().forEach((a) => a.cancel());
    root.style.visibility = 'hidden';
    attach.style.transform = '';
    contactWrap.style.transform = '';
    clip.style.clipPath = '';
  }

  private startTick() {
    if (!this.raf) this.raf = requestAnimationFrame(this.tick);
  }

  private begin(v: Visit, track: PinnedTrack, dir: 'up' | 'down' | 'left') {
    this.visit = v;
    this.ensureBody()?.pace?.(v.k);
    const unsub = v.req.port.onChange(() => this.homeChanged());
    const done = v.resolve;
    v.resolve = () => {
      unsub();
      done();
    };
    v.req.port.release(dir).then(() => {
      if (this.visit !== v) {
        // Визит сняли, пока дом отдавал ядро: ядро возвращается домой без полёта.
        v.req.port.receive();
        return;
      }
      const o = track.samples[0];
      // Пока ядро отдавалось, открыли слой поверх или сменился дом. Дом тот же или спрятан — ядро
      // возвращается без полёта, капля не показывается; другой — капля идёт к нему от прежнего дома.
      const moved = v.homeMoved ? this.fromOld(v) : null;
      if (v.homeMoved && !moved) return this.finish(v);
      // Кадр передачи: ядро в доме скрыто — капля встаёт на его место в этом же кадре, в его тоне.
      const { root, attach } = this.L;
      root.getAnimations().forEach((a) => a.cancel());
      root.style.transform = `translate(${o.x}px, ${o.y}px)`;
      attach.style.transform = '';
      this.snapTone(v);
      root.style.visibility = 'visible';
      this.onAway(true);
      this.startTick();
      if (moved) {
        v.phase = 'back';
        return this.homeLegs(v, moved.pts, 0, moved.to);
      }
      v.phase = 'out';
      this.travel(v, track, 'out');
    });
  }

  /** Тон дома — капле в кадр передачи, без перехода: шва цвета нет; переливы тона — только в пути. */
  private snapTone(v: Visit) {
    const { clip } = this.L;
    v.tone = v.req.port.origin()?.tone ?? 'brand';
    clip.style.removeProperty('--courier-tone-cap');
    clip.dataset.snap = '';
    this.setTone(v.tone);
    // Стиль считается с выключенными переходами; дальше они снова плавные.
    void getComputedStyle(clip).getPropertyValue('--courier-tone');
    for (const el of Array.from(clip.querySelectorAll('[data-tone-layer]')))
      void getComputedStyle(el).opacity;
    delete clip.dataset.snap;
  }

  /** Дом сменился, пока ядро отдавалось: путь от прежнего дома к нынешнему (его выходы — на план). */
  private fromOld(v: Visit): { pts: PathPoint[]; to: HomePoint } | null {
    const o = v.req.port.origin();
    if (!o || !o.shown || Math.hypot(o.x - v.from.x, o.y - v.from.y) < 6) return null;
    const read = readPlan(v.req.port);
    const pts = read && routeFromOld(read.plan, v.from, null);
    return pts ? { pts, to: { ...read.plan.home } } : null;
  }

  /**
   * Дорога домой ногами: с точками возврата — по кускам, перед каждым разворотом остановка (A_BRAKE), дома —
   * A_STOP_HOME. Короткая дорога с места растягивается до 420 мс, как вылет.
   */
  private homeLegs(v: Visit, pts: PathPoint[], v0: number, to: HomePoint) {
    const [first, ...next] = splitCusps(pts);
    if (!first) return this.finish(v);
    this.travel(v, this.homeTrack(first, v0, next.length === 0), 'back', { to, next });
  }

  private homeTrack(pts: PathPoint[], v0: number, last: boolean): PinnedTrack {
    return buildTrack(pts, {
      v0,
      vc: cruise(lengthOf(pts), true),
      aStop: last ? A_STOP_HOME : A_BRAKE,
      minMs: v0 > 0.05 || !last ? undefined : ROUTE_MIN_MS,
    });
  }

  /** Путь по трассе: корень — translate по кадрам, тело — форма на тех же часах. */
  private travel(
    v: Visit,
    track: PinnedTrack,
    kind: Leg['kind'],
    more: Pick<Leg, 'then' | 'ahead' | 'to' | 'next'> = {},
  ) {
    const { root, tilt, skin } = this.L;
    const prev = v.leg;
    // Пройденное от дома — путь назад, если улиц плана рядом не окажется (цель пропала, дом сменился).
    if (prev && !prev.home) v.trail = join(v.trail, this.passed(v, prev));
    prev?.anims.forEach((a) => a.cancel());
    const rate = 1 / v.k;
    const main = root.animate(rootFrames(bakeKeys(track), track.duration), {
      duration: track.duration,
      fill: 'forwards',
    });
    const anims: Animation[] = [main];
    try {
      const shape = this.body?.travel(track, v.req.settings.motion);
      if (shape?.tilt) anims.push(tilt.animate(shape.tilt, { duration: track.duration }));
      if (shape?.skin) anims.push(skin.animate(shape.skin, { duration: track.duration }));
    } catch {
      // Тело сменное: его сбой не оставляет визит без ноги — путь идёт без кадров формы.
    }
    for (const a of anims) a.playbackRate = rate;
    const home = kind === 'back' || (kind === 'brake' && !!prev?.home);
    const leg: Leg = { track, anims, kind, home, ...more };
    v.leg = leg;
    main.onfinish = () => {
      if (this.visit === v && v.leg === leg) this.arrive(v, leg);
    };
    if (kind === 'out' && track.duration > RECHECK_MS) {
      this.later(v, () => this.recheck(v, leg), (track.duration - RECHECK_MS) * v.k);
    }
  }

  private arrive(v: Visit, leg: Leg) {
    if (leg.kind === 'brake') return this.afterBrake(v, leg, performance.now());
    if (leg.kind === 'out') return this.touch(v);
    const [piece, ...next] = leg.next ?? [];
    // Разворот на дороге домой: встали — дальше с места.
    if (piece)
      return this.travel(v, this.homeTrack(piece, 0, next.length === 0), 'back', { to: leg.to, next });
    this.receive(v);
  }

  /**
   * После торможения раскладка может ещё ехать (выезд зоны 480 мс). К цели заново — когда встанет. Домой:
   * у стены окна (она едет с окном) капля ждёт на своей улице; на крыше, в меню и над содержимым — идёт сразу:
   * крыша и коридор от раскладки не зависят, выход над окном — тоже.
   */
  private afterBrake(v: Visit, leg: Leg, since: number) {
    if (this.visit !== v || v.leg !== leg) return;
    const then = leg.then ?? 'home';
    const end = leg.track.samples.length - 1;
    const lane = leg.track.samples[end].lane;
    const pin = leg.track.pins[end] ?? 0;
    const inGap = pin > 0.01 && pin <= 1 && lane !== 'hall' && lane !== 'sill';
    // Окно ещё сдвигается (переход мог начаться позже метки перехода) — тоже ждём.
    const pane = v.target.pane?.isConnected ? v.target.pane.getBoundingClientRect().left : null;
    const sliding = pane !== null && v.paneLeft !== null && Math.abs(pane - v.paneLeft) > 0.5;
    v.paneLeft = pane;
    if (
      (then === 'tail' || inGap) &&
      (layoutMoving(v.shell) || sliding || performance.now() - since < SETTLE_TICK_MS) &&
      performance.now() - since < SETTLE_MAX_MS
    ) {
      this.later(v, () => this.afterBrake(v, leg, since), SETTLE_TICK_MS);
      return;
    }
    if (then === 'tail') this.tail(v, 0);
    else this.goHome(v, null, 0);
  }

  /** Перепроверка за 120 мс до касания: точка посадки и место по нормали. */
  private recheck(v: Visit, leg: Leg) {
    if (this.visit !== v || v.leg !== leg || v.phase !== 'out' || dropOnly(v.seat.kind)) return;
    const read = readPlan(v.req.port);
    if (!read || !shown(v.target.el)) return this.abort(v);
    const seat = { ...v.seat, x: v.seat.x + v.shift.ax, y: v.seat.y + v.shift.ay };
    const clip = this.clipOf(read, v.target, seat);
    if (makeProbe(read).landing(seat, v.size, v.room, clip, this.anchorEl(v.target))) this.abort(v);
  }

  /** Касание: мениск, оседание и разворот тела; кольцо и контур — только с «Кольцом на цели». */
  private touch(v: Visit) {
    const leg = v.leg!;
    v.phase = 'contact';
    const s = leg.track.samples;
    const before = s[Math.max(0, s.length - 21)];
    const c: Contact = {
      kind: v.seat.kind,
      x: v.seat.x + v.shift.ax,
      y: v.seat.y + v.shift.ay,
      normal: v.seat.normal,
      size: v.size,
      room: v.room,
      impact: Math.min(1, before.speed / Math.max(0.001, cruise(leg.track.length))),
      anchor: v.target.el,
    };
    v.contact = c;
    v.landed = leg.track;
    v.leg = null;
    // Тело и плёнка — в видимой части прокрутки окна цели: на полосу вкладок не залезают.
    if (v.target.scroll) {
      const r = v.target.scroll.getBoundingClientRect();
      this.L.clip.style.clipPath = `inset(${r.top}px ${window.innerWidth - r.right}px ${window.innerHeight - r.bottom}px ${r.left}px)`;
    }
    // Плёнка — в координатах планирования: слой contact едет за целью вместе с attach.
    const drawn: Contact = { ...c, x: v.seat.x, y: v.seat.y };
    if (!dropOnly(c.kind)) v.films.meniscus = this.contact.meniscus(drawn, v.k);
    const plan = EVENT_PLAN[v.req.kind];
    if (v.req.settings.targetRing) {
      if (v.target.row) v.films.contour = this.contact.contour(v.target.row, plan.ring, v.k);
      else if (!dropOnly(c.kind)) v.films.ring = this.contact.ring(v.target.el, drawn, plan.ring, v.k);
    }
    void (this.body ? this.body.land(c) : Promise.resolve()).then(() => {
      if (this.visit === v && v.phase === 'contact') this.hold(v);
    });
  }

  private hold(v: Visit) {
    v.phase = 'hold';
    const now = performance.now();
    const plan = EVENT_PLAN[v.req.kind];
    // Событие старше пришло в пути или на касании: удержание — 400 мс, жест его не продлевает.
    const holdMs = v.hurried ? HOLD_MIN_MS : plan.holdMs;
    v.landedAt = now;
    v.holdEnd = now + holdMs * v.k;
    v.reactUntil = v.hurried ? v.holdEnd : Infinity;
    const c = v.contact!;
    const reacted = this.body
      ? this.body.react(plan.drop ?? 'nod', c, dropOnly(c.kind) ? null : plan.finale)
      : null;
    (reacted ?? Promise.resolve()).then(() => {
      if (this.visit !== v) return;
      v.reactUntil = performance.now();
      this.maybeLift(v);
    });
    this.later(v, () => this.maybeLift(v), holdMs * v.k);
    // Жест продлевает удержание не больше чем на 800 мс.
    this.later(v, () => this.maybeLift(v, true), (holdMs + (v.hurried ? 0 : REACT_EXTRA_MAX_MS)) * v.k);
    // Пока садилась, сменился дом или открылся слой поверх: удержания нет — сразу подъём.
    if (v.homeMoved) this.lift(v);
  }

  /** Подъём, когда удержание кончилось и жест доигран. */
  private maybeLift(v: Visit, force = false) {
    if (this.visit !== v || v.phase !== 'hold') return;
    const now = performance.now();
    if (!force && (now < v.holdEnd - 1 || now < v.reactUntil - 1)) return;
    this.lift(v);
  }

  /** Подъём: кольцо стекает в точку касания, тело сворачивается с 80-й мс, мениск втягивается. */
  private lift(v: Visit) {
    if (v.phase !== 'hold' && v.phase !== 'contact') return;
    if (v.phase === 'contact') {
      // Тело ещё садится: подъём — сразу после посадки.
      v.homeMoved = true;
      return;
    }
    v.phase = 'lift';
    const k = v.k;
    const c = v.contact!;
    const ring = v.films.ring?.back(k);
    const contour = v.films.contour?.off(k);
    const folded = new Promise<void>((r) =>
      this.later(v, () => void (this.body ? this.body.lift(c) : Promise.resolve()).then(r), LIFT_LAG_MS * k),
    );
    const film = v.films.meniscus?.out(k);
    void Promise.all([ring, contour, folded, film]).then(() => {
      if (this.visit !== v) return;
      v.films = {};
      this.contact.clear();
      this.L.clip.style.clipPath = '';
      this.goHome(v, null, 0);
    });
  }

  /** Цель на экране сейчас: перечитать по адресу и роли (прокрутка, смена раскладки). */
  private reread(v: Visit, read: PlanRead) {
    if (!shown(v.target.el)) return null;
    const t = readTarget(read, v.id, v.target.el, v.meta);
    if (typeof t === 'string') return null;
    const seat = seatFor(read.plan, t.geom, v.req.settings.seat);
    return typeof seat === 'string' ? null : { t, seat };
  }

  /**
   * Домой — кратчайшим путём по графу к текущему дому из точки, где капля сейчас (с поправкой attach).
   * heading — курс, с которым она уже идёт (null — стоит); v0 — её скорость. Улиц плана рядом нет (цель
   * ушла со страницы, дом сменился, посадка уехала под полосу): стоя — назад по пройденному до улицы плана;
   * на ходу — вперёд по дороге домой, а если капля шла к цели — торможение и разворот. Гаснуть вдали от дома
   * капле нельзя (spec-3-motion §8): это только «Фокус» и вкладка в фоне.
   */
  private goHome(v: Visit, heading: number | null, v0: number, keep = false) {
    if (this.visit !== v) return;
    const pos = this.position(v);
    const read = readPlan(v.req.port);
    if (!read) return this.finish(v);
    const at = this.reread(v, read);
    const moving = v0 > 0.05;
    const g = buildGraph(read.plan, at?.seat ?? null);
    const here = attachPoint(g, pos.x, pos.y, 12);
    let pts = here ? (findRoute(g, here, 'home', moving ? heading : null)?.pts ?? null) : null;
    // Вне улиц: над окном — полкой на своей высоте к стене окна, как подход к цели наоборот (стена — нынешняя:
    // прежняя дорога могла остаться под выехавшей зоной); у стены или на пороге — они тянутся до капли. На ходу —
    // если такая дорога ведёт по курсу.
    const exit = pts ? null : exitOf(read.plan, pos.x, pos.y);
    // Капля на самой цели-блоке (цель ушла со страницы): подход наоборот — спуском вверх на полку над кромкой,
    // потом полкой к стене; на своей высоте она шла бы по ряду новой страницы.
    if (exit?.seat && !v.leg && v.seat.kind === 'block') exit.seat.shelfY = pos.y - SHELF_DY;
    if (exit) {
      const ge = buildGraph(read.plan, exit.seat, exit.extra);
      const from = exit.seat ? ge.seat : attachPoint(ge, pos.x, pos.y, 12);
      const out = from ? findRoute(ge, from, 'home', moving ? heading : null) : null;
      pts = out ? easeExit(out.pts) : null;
      if (pts && layoutMoving(v.shell)) v.relayoutAt = performance.now();
    }
    if (!pts && (!moving || (!here && v.leg?.home))) {
      pts = this.homeward(v, read, moving ? heading : null);
      // Раскладка ещё едет: когда встанет, остаток пути пересоберётся по ней (tick).
      if (pts && layoutMoving(v.shell)) v.relayoutAt = performance.now();
    }
    if (!pts) {
      // Пересборка после раскладки: лучшей дороги нет — капля идёт прежней.
      if (keep) return;
      // Дальше по ходу к дому не свернуть: тормозим по той же дороге и разворачиваемся.
      if (moving && v.leg) return this.brakeThen(v, 'home', A_BRAKE);
      return this.finish(v);
    }
    // Новая трасса — в нынешних координатах: поправка attach обнуляется в том же кадре.
    this.rebase(v, at?.t ?? v.target);
    v.phase = 'back';
    this.homeLegs(v, pts, v0, { ...read.plan.home });
  }

  /**
   * Дорога домой без улиц рядом: от точки, где капля сейчас, — по остатку дороги домой или назад по пройденному
   * до первой улицы плана (пока раскладка едет — только рамки: крыша и коридор не двигаются), оттуда кратчайшим
   * путём. Дошла по пройденному до прежнего дома — дальше его выходами. Развороты в пути режет homeLegs.
   */
  private homeward(v: Visit, read: PlanRead, heading: number | null): PathPoint[] | null {
    const leg = v.leg;
    let way: PathPoint[];
    let old: HomePoint;
    if (leg?.home) {
      const i = indexAt(leg.track, this.legTime(leg));
      way = this.shifted(v, join(this.ptsOf(leg.track, i), leg.ahead ?? [], ...(leg.next ?? [])));
      old = leg.to ?? v.from;
    } else {
      const now = leg ? this.passed(v, leg) : v.landed ? this.shifted(v, this.ptsOf(v.landed)) : [];
      way = reversed(join(v.trail, now));
      old = v.from;
    }
    if (way.length === 0) return null;
    const frameOnly = layoutMoving(v.shell);
    let g = buildGraph(read.plan, null);
    let near = streetsNear(g, JOIN_PX);
    for (let i = 0; i < way.length; i++) {
      const p = way[i];
      if ((frameOnly && p.pin > 0.01) || !near(p.x, p.y)) continue;
      const here = attachPoint(g, p.x, p.y, JOIN_PX);
      // Дальше по ходу не свернуть — разворот на стыке (homeLegs остановит каплю перед ним).
      const route = here
        ? (findRoute(g, here, 'home', i === 0 ? heading : p.h) ?? (i > 0 ? findRoute(g, here, 'home') : null))
        : null;
      if (route) return join(way.slice(0, i + 1), route.pts);
      g = buildGraph(read.plan, null);
      near = streetsNear(g, JOIN_PX);
      i += JOIN_SKIP;
    }
    const end = way[way.length - 1];
    const home = read.plan.home;
    // Прежняя дорога и так приходит в нынешний дом — по ней.
    if (Math.hypot(end.x - home.x, end.y - home.y) < 6) return way.length > 1 ? way : null;
    // Дошли по пройденному до прежнего дома: дальше — его выходами к нынешнему.
    if (Math.hypot(end.x - old.x, end.y - old.y) >= 6) return null;
    const rest = routeFromOld(read.plan, old, way.length > 1 ? end.h : heading);
    return rest ? join(way, rest) : null;
  }

  /** Точки трассы i0…i1 как точки пути (в координатах ноги, без поправки attach). */
  private ptsOf(track: PinnedTrack, i0 = 0, i1 = track.samples.length - 1): PathPoint[] {
    const out: PathPoint[] = [];
    for (let i = i0; i <= i1; i++) {
      const s = track.samples[i];
      out.push({
        x: s.x,
        y: s.y,
        h: s.heading,
        k: s.curvature,
        lane: s.lane,
        normal: s.normal,
        room: s.room,
        pin: track.pins[i] ?? 0,
      });
    }
    return out;
  }

  /** Точки пути — на экране сейчас: с поправкой attach по их привязке. */
  private shifted(v: Visit, pts: PathPoint[]): PathPoint[] {
    const { px, ax, ay, mx } = v.shift;
    if (!px && !ax && !ay && !mx) return pts;
    return pts.map((p) => {
      const o = offsetOf(p.pin, p.lane, v.shift);
      return { ...p, x: p.x + o.x, y: p.y + o.y };
    });
  }

  /** Пройденное по ноге до этого мига — на экране сейчас. */
  private passed(v: Visit, leg: Leg): PathPoint[] {
    return this.shifted(v, this.ptsOf(leg.track, 0, indexAt(leg.track, this.legTime(leg))));
  }

  /** Хвост к цели заново (прокрутка увела её больше чем на 24 px, раскладка встала) — с той же скоростью. */
  private tail(v: Visit, v0: number) {
    const read = readPlan(v.req.port);
    const at = read && this.reread(v, read);
    if (!read || !at) return this.abort(v);
    const pos = this.position(v);
    const g = buildGraph(read.plan, at.seat);
    const here = attachPoint(g, pos.x, pos.y, 12);
    const heading = v.leg ? v.leg.track.at(this.legTime(v.leg)).heading : null;
    const moving = v0 > 0.05;
    const route = here && g.seat ? findRoute(g, here, g.seat, moving ? heading : null) : null;
    // Стоя пути к цели нет — домой (abort здесь не помог бы: торможение уже позади).
    if (!route) return moving ? this.brakeThen(v, 'tail', A_BRAKE) : this.goHome(v, null, 0);
    this.rebase(v, at.t);
    v.target = at.t;
    v.seat = at.seat;
    const pts = at.seat.kind === 'menu' ? pinTail(route.pts, HOME_SKIP) : route.pts;
    this.travel(v, buildTrack(pts, { v0, vc: cruise(route.length), aStop: A_STOP }), 'out');
  }

  /** Тормозить по той же дороге до остановки, потом — домой или к цели заново. */
  private brakeThen(v: Visit, then: 'home' | 'tail', a: number) {
    const leg = v.leg;
    if (!leg || leg.kind === 'brake') return;
    const i = indexAt(leg.track, this.legTime(leg));
    const brake = brakeTrack(leg.track, i, a);
    // На дороге домой — её остаток за точкой остановки: по нему можно дойти, если улиц рядом не окажется.
    const ahead = leg.home
      ? this.ptsOf(leg.track, Math.min(leg.track.samples.length - 1, i + brake.samples.length - 1))
      : undefined;
    this.travel(v, brake, 'brake', { then, ahead, to: leg.to, next: leg.home ? leg.next : undefined });
  }

  /** Цель пропала или посадка не прошла перепроверку: торможение не дольше 125 мс, домой без посадки. */
  private abort(v: Visit) {
    if (this.visit !== v) return;
    const leg = v.leg;
    if (v.phase === 'out' && leg?.kind === 'brake') {
      // Торможение «к цели заново» — теперь домой; уже доиграно (ждали раскладку) — домой с места.
      leg.then = 'home';
      if (this.legTime(leg) >= leg.track.duration - 1) {
        v.phase = 'back';
        this.goHome(v, null, 0);
      }
      return;
    }
    if (v.phase === 'out') {
      v.phase = 'back';
      return this.brakeThen(v, 'home', A_ABORT);
    }
    if (v.phase === 'contact' || v.phase === 'hold') this.lift(v);
  }

  private legTime(leg: Leg) {
    return Number(leg.anims[0].currentTime ?? 0);
  }

  /** Поправка attach у капли сейчас (на касании и удержании — сдвиг цели). */
  private offset(v: Visit) {
    const leg = v.leg;
    if (!leg) return { x: v.shift.ax, y: v.shift.ay };
    const i = indexAt(leg.track, this.legTime(leg));
    return offsetOf(leg.track.pins[i] ?? 0, leg.track.samples[i].lane, v.shift);
  }

  /** Где капля сейчас на экране: трасса + поправка attach. */
  private position(v: Visit) {
    const o = this.offset(v);
    if (v.leg) {
      const s = v.leg.track.at(this.legTime(v.leg));
      return { x: s.x + o.x, y: s.y + o.y };
    }
    return { x: v.seat.x + o.x, y: v.seat.y + o.y };
  }

  /** Трасса строится в нынешних координатах: базовые прямоугольники — сейчас, поправка — ноль. */
  private rebase(v: Visit, t: TargetRead) {
    const el = this.anchorEl(t);
    // Окно — пока оно в разметке (цель могла уйти, окно осталось: стена держится за него), якорь — пока виден.
    v.base = {
      anchor: shown(el) ? boxOf(el.getBoundingClientRect()) : v.base.anchor,
      pane: t.pane?.isConnected ? this.paneBox(t) : v.base.pane,
      menu: menuRight(v.shell) ?? v.base.menu,
    };
    v.shift = { ax: 0, ay: 0, px: 0, mx: 0 };
    this.L.attach.style.transform = '';
    this.L.contactWrap.style.transform = '';
  }

  /** Приём дома; дом сменился в последний миг — дойти до нового (один раз). */
  private receive(v: Visit) {
    const o = v.req.port.origin();
    const pos = this.position(v);
    if (o && o.shown && !v.retried && Math.hypot(o.x - pos.x, o.y - pos.y) > 6) {
      v.retried = true;
      return this.goHome(v, null, 0);
    }
    this.finish(v);
  }

  /** Конец визита: дом принимает ядро в одном кадре со скрытием капли. */
  private finish(v: Visit) {
    if (this.visit !== v) return;
    const was = v.phase;
    v.phase = 'done';
    v.timers.forEach((t) => window.clearTimeout(t));
    v.leg?.anims.forEach((a) => a.cancel());
    v.films.ring?.off();
    if (!v.received) {
      v.received = true;
      void v.req.port.receive();
    }
    this.contact.clear();
    this.hideDrop();
    // Сняли у цели (вкладка в фон, «Фокус»): тело могло остаться развёрнутым — снимается целиком, следующий
    // визит смонтирует его заново каплей. Тело сменили во время визита — новое встаёт сейчас.
    if (was === 'contact' || was === 'hold' || was === 'lift' || this.nextBody) this.dropBody();
    if (this.nextBody) {
      this.body = this.nextBody;
      this.nextBody = null;
    }
    this.visit = null;
    this.onAway(false);
    v.resolve();
  }

  /* ----- Прерывания ----- */

  /** Дом сменился (переход по меню, «Фокус») или только раскладка (меню свернули или раскрыли). */
  private homeChanged() {
    const v = this.visit;
    if (!v) return;
    const o = v.req.port.origin();
    if (o && !o.shown) return this.absorb(v);
    const h = v.homeAt;
    if (o && h && o.kind === h.kind && Math.hypot(o.x - h.x, o.y - h.y) < 2) {
      // Дом тот же: стена и полка держатся за окно (attach), порог тянется с меню; путь — после перехода.
      v.relayoutAt = performance.now();
      this.dirty = true;
      return;
    }
    if (o) v.homeAt = { kind: o.kind, x: o.x, y: o.y };
    switch (v.phase) {
      case 'prep':
      case 'contact':
        v.homeMoved = true;
        return;
      case 'hold':
        return this.lift(v);
      case 'out':
      case 'back': {
        const leg = v.leg;
        if (!leg || leg.kind === 'brake') {
          if (leg) leg.then = 'home';
          return;
        }
        const i = indexAt(leg.track, this.legTime(leg));
        const s = leg.track.at(this.legTime(leg));
        v.phase = 'back';
        // На стене или полке, пока раскладка едет, — сначала встать, потом домой. В коридоре (его конец
        // привязан к пункту) — сразу: левая кромка меню и высоты пунктов от раскладки не зависят.
        if (leg.track.pins[i] > 0 && s.lane !== 'hall' && layoutMoving(v.shell))
          return this.brakeThen(v, 'home', A_BRAKE);
        return this.goHome(v, s.heading, s.speed);
      }
    }
  }

  /** Ящик, поиск, ИИ поверх: сразу домой. */
  suspend() {
    const v = this.visit;
    if (!v) return;
    if (v.phase === 'out' && v.leg && v.leg.kind !== 'brake') {
      v.phase = 'back';
      this.brakeThen(v, 'home', A_BRAKE);
    } else if (v.phase === 'out' && v.leg?.kind === 'brake') this.abort(v);
    else if (v.phase === 'hold') this.lift(v);
    else if (v.phase === 'prep' || v.phase === 'contact') v.homeMoved = true;
  }

  /** Пришло событие старше: удержание не короче 400 мс после посадки (пришло раньше удержания — помним). */
  hurry() {
    const v = this.visit;
    if (!v) return;
    v.hurried = true;
    if (v.phase === 'hold') {
      v.holdEnd = Math.min(v.holdEnd, v.landedAt + HOLD_MIN_MS * v.k);
      v.reactUntil = Math.min(v.reactUntil, v.holdEnd);
      this.later(v, () => this.maybeLift(v), Math.max(0, v.holdEnd - performance.now()));
    }
  }

  /** Новое событие на ту же цель, пока капля на ней: второго полёта нет — жест на месте. */
  replay(els: readonly Element[], kind: ObserverEventKind): boolean {
    const v = this.visit;
    if (!v || !v.contact || (v.phase !== 'hold' && v.phase !== 'contact') || !els.includes(v.target.el))
      return false;
    const plan = EVENT_PLAN[kind];
    if (v.phase === 'hold')
      void this.body?.react(plan.drop ?? 'nod', v.contact, dropOnly(v.contact.kind) ? null : plan.finale);
    return true;
  }

  /** «Фокус» во время визита — единственное гашение: впитаться в кромку за 160 мс, ядро — домой без полёта. */
  private absorb(v: Visit) {
    if (v.phase === 'prep') {
      v.homeMoved = true;
      return;
    }
    if (v.phase === 'done') return;
    v.phase = 'done';
    v.timers.forEach((t) => window.clearTimeout(t));
    v.timers = [];
    v.leg?.anims.forEach((a) => a.pause());
    v.films.ring?.off();
    const fade = this.L.attach.animate([{ scale: '1' }, { scale: '0' }], {
      duration: ABSORB_MS * v.k,
      easing: 'ease-in',
      fill: 'forwards',
    });
    void fade.finished.finally(() => {
      v.phase = 'lift';
      this.finish(v);
    });
  }

  /** Вкладка браузера ушла в фон: визит снимается мгновенно, ядро дома, признаки события остаются. */
  cancel() {
    const v = this.visit;
    if (v) this.finish(v);
  }

  /** Курсор вошёл в цель во время удержания: кольцо снимается сразу, возврат по расписанию. */
  pointer(x: number, y: number) {
    const v = this.visit;
    if (!v || (v.phase !== 'hold' && v.phase !== 'contact') || !v.films.ring) return;
    if (inBox(boxOf(v.target.el.getBoundingClientRect()), x, y)) {
      v.films.ring.off();
      v.films.ring = undefined;
    }
  }

  /* ----- Кадровый тик визита: attach, сдвиги, видимость посадки ----- */

  private tick = () => {
    const v = this.visit;
    if (!v) {
      this.raf = 0;
      return;
    }
    if (layoutMoving(v.shell)) this.dirty = true;
    this.followTone(v);
    if (this.dirty) {
      this.dirty = false;
      const el = this.anchorEl(v.target);
      const pane = v.target.pane;
      // Стена держится за окно и тогда, когда цели уже нет (переход по меню: выехала зона, окно сдвинулось).
      const px =
        v.base.pane && pane?.isConnected ? pane.getBoundingClientRect().left - v.base.pane.left : v.shift.px;
      const m = menuRight(v.shell);
      const mx = m !== null && v.base.menu !== null ? m - v.base.menu : v.shift.mx;
      if (shown(el)) {
        const r = el.getBoundingClientRect();
        v.shift = { ax: r.left - v.base.anchor.left, ay: r.top - v.base.anchor.top, px, mx };
      } else {
        v.shift = { ...v.shift, px, mx };
        if (v.phase === 'out' || v.phase === 'hold') this.abort(v);
      }
    }
    const { x, y } = this.offset(v);
    this.L.attach.style.transform = x || y ? `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)` : '';
    this.L.contactWrap.style.transform =
      v.shift.ax || v.shift.ay ? `translate(${v.shift.ax.toFixed(2)}px, ${v.shift.ay.toFixed(2)}px)` : '';
    const leg = v.leg;
    // Раскладка встала после перехода: остаток пути — по новой, с той же скоростью.
    if (
      v.relayoutAt !== null &&
      performance.now() - v.relayoutAt > RELAYOUT_MIN_MS &&
      !layoutMoving(v.shell)
    ) {
      v.relayoutAt = null;
      if (leg && leg.kind !== 'brake') {
        const s = leg.track.at(this.legTime(leg));
        if (v.phase === 'out') this.tail(v, s.speed);
        else if (v.phase === 'back') this.goHome(v, s.heading, s.speed, true);
      }
    }
    if (leg && v.leg === leg) {
      const s = leg.track.at(this.legTime(leg));
      this.body?.onFrame?.(s);
      // Прокрутка увела цель больше чем на 24 px — хвост заново (во время перехода раскладки — после него).
      if (
        v.phase === 'out' &&
        leg.kind === 'out' &&
        v.relayoutAt === null &&
        !layoutMoving(v.shell) &&
        Math.hypot(v.shift.ax, v.shift.ay) > REPLAN_SCROLL_PX
      ) {
        this.tail(v, s.speed);
      }
    } else if (v.phase === 'hold' && v.target.scroll) {
      // Точка посадки ушла из видимой части — сразу подъём и возврат.
      if (
        !inBox(boxOf(v.target.scroll.getBoundingClientRect()), v.seat.x + v.shift.ax, v.seat.y + v.shift.ay)
      ) {
        this.lift(v);
      }
    }
    this.raf = requestAnimationFrame(this.tick);
  };
}
